import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Ticket, Heart, ArrowUp, Star } from 'lucide-react';
import { type DashboardData } from '@/services/dashboardService';
import { useAuth } from '@/contexts/UserContext';
import { imageSrc } from '@/utils/event';
import type { Event } from '@/types/event';
import EventWrapper from './EventWrapper';
import './ProfessionalNetworking.css';

interface ProfessionalNetworkingProps {
  userData: DashboardData;
  events?: Event[];
  loading?: boolean;
  error?: string | null;  
}

const ProfessionalNetworking: React.FC<ProfessionalNetworkingProps> = ({
  userData, 
  events = [],
  loading = false,
  error = null
}) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return { day: '', month: '', year: '', time: '' };
    
    const date = new Date(dateString);
    return {
      day: date.getDate().toString(),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      year: date.getFullYear().toString(),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    };
  };

  // Function to handle Get Ticket click
  const handleGetTicketClick = (event: Event, e: React.MouseEvent) => {
    e.preventDefault();
    const eventId = event.id;
    const scheduleId = event.next_schedule?.id;
    
    // Navigate to Professional Networking page with all necessary parameters
    const params = new URLSearchParams();
    params.set('eventId', eventId.toString());
    params.set('action', 'reservation');
    
    if (scheduleId) {
      params.set('scheduleId', scheduleId.toString());
    }
    
    navigate(`/professional-networking?${params.toString()}`);
  };

  // Function to handle Arrow Up click (open event modal)
  const handleArrowUpClick = (event: Event, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const eventId = event.id;
    const scheduleId = event.next_schedule?.id;
    
    // Navigate to Professional Networking page with all necessary parameters
    const params = new URLSearchParams();
    params.set('eventId', eventId.toString());
    params.set('action', 'details');
    
    if (scheduleId) {
      params.set('scheduleId', scheduleId.toString());
    }
    
    navigate(`/professional-networking?${params.toString()}`);
  };

  const getEventCardClasses = (eventId: string, index: number) => {
    const baseClasses = "relative bg-gradient-to-b from-black/0 to-black rounded-2xl overflow-hidden transition-all duration-1000 ease-in-out h-96";
    
    // For small screens, always use single column
    const mobileClasses = "w-full col-span-1";
    
    // For medium+ screens, apply the responsive behavior
    const desktopClasses = `
      md:col-span-1 
      ${!hoveredCard && index === 0 ? 'md:col-span-2' : ''}
      ${hoveredCard === eventId ? 'md:col-span-2' : ''}
      ${hoveredCard && hoveredCard !== eventId ? 'md:col-span-1' : ''}
    `;

    return `${baseClasses} ${mobileClasses} ${desktopClasses}`;
  };

  const BigCardContent: React.FC<{ event: Event }> = ({ event }) => {
    const { day, month, year, time } = formatDate(event.event_date || event.start_date);
    const finalImageSrc = imageSrc(event, 570, 362);
    let tag = 'Networking';

    if (event?.taggings?.length) {
      tag = event?.taggings[0]?.name || event?.taggings[0]?.slug || event?.taggings[0]?.description || 'Networking' as string | null;
    }

    return (
      <>
        <div className="absolute top-4 left-3 bg-purple-100 rounded-t-sm border-2 border-purple-300 overflow-hidden w-16 h-16">
          <div className="text-center pt-1 text-blue-950 text-xs font-medium">
            {month}
          </div>
          <div className="text-center text-blue-950 text-2xl font-bold leading-6">
            {day}
          </div>
          <div className="text-center text-blue-950 text-xs font-medium">
            {year}
          </div>
        </div>

        {/* Time Badge */}
        <div className="absolute top-20 left-3 bg-purple-900 rounded-b-sm border-2 border-purple-500 w-16 h-7">
          <div className="text-center text-white text-xs font-medium pt-1">
            {time}
          </div>
        </div>

        <div className="absolute line-clamp-1 bottom-16 left-0 bg-yellow-300 rounded-tr-lg px-3 py-1 w-36">
          <div className="text-gray-800 text-sm font-semibold">
            {tag}
          </div>
        </div>

        <button 
          onClick={(e) => handleGetTicketClick(event, e)}
          className="absolute bottom-16 left-40 bg-white rounded-md px-4 py-2 flex items-center gap-2 hover:bg-gray-100 transition-colors cursor-pointer shadow-md hover:shadow-lg font-semibold"
        >
          <span className="text-black text-sm font-semibold">
            Get ticket
          </span>
          <Ticket size={16} color="black" className="-rotate-45" />
        </button>

        <div className="absolute bottom-0 left-5 right-5">
          <h3 className="line-clamp-2 text-white text-xl font-normal leading-8">
            {event.title}
          </h3>
        </div>

        {event.is_featured && (
          <div className="absolute bottom-5 right-5">          
            <Star size={26} color="white" />
          </div>
        )}

        <button 
          onClick={(e) => handleArrowUpClick(event, e)}
          className="absolute top-3 right-3 bg-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
        >
          <ArrowUp size={20} color="black" className="rotate-45" />
        </button>
      </>
    );
  };

  const SmallCardContent: React.FC<{ event: Event }> = ({ event }) => {
    const isUpcoming = event.is_upcoming || (event.event_date && new Date(event.event_date) > new Date());
    let tag = 'Networking';

    if (event?.taggings?.length) {
      tag = event?.taggings[0]?.name || event?.taggings[0]?.slug || event?.taggings[0]?.description || 'Networking' as string | null;
    }

    return (
      <>
        {isUpcoming && (
          <div className="absolute top-2 left-2 bg-white rounded-lg px-3 py-1">
            <div className="text-black text-sm font-medium">
              Upcoming next month
            </div>
          </div>
        )}

        <div className="absolute line-clamp-1 bottom-16 left-0 bg-yellow-300 rounded-tr-lg px-3 py-1">
          <div className="text-gray-800 text-sm font-semibold">
            {tag}
          </div>
        </div>

        <div className="absolute bottom-5 left-5 right-5">
          <h3 className="line-clamp-2 text-white text-xl font-normal leading-8">
            {event.title}
          </h3>
        </div>

        {event.interested && (
          <div className="absolute bottom-4 right-4 flex items-center gap-1">
            <Heart size={16} color="white" fill="white" />
            <span className="text-white text-sm">
              {typeof event.interested === 'string' ? event.interested : event.interested.toLocaleString()}
            </span>
          </div>
        )}
      </>
    );
  };

  const EventCard: React.FC<{ event: Event; index: number }> = ({ event, index }) => {
    const isBigCard = (!hoveredCard && index === 0) || hoveredCard === event.id.toString();
    const finalImageSrc = imageSrc(event, isBigCard ? 570 : 256, isBigCard ? 362 : 384);
    
    return (
      <div
        className={getEventCardClasses(event.id.toString(), index)}
        onMouseEnter={() => setHoveredCard(event.id.toString())}
        onMouseLeave={() => setHoveredCard(null)}
      >
        <img 
          className="w-full h-full object-cover absolute inset-0" 
          src={finalImageSrc} 
          alt={event.title}
          loading="lazy"
        />
        
        <div className={`absolute inset-0 bg-gradient-to-b from-transparent ${isBigCard ? 'to-black/80' : 'via-black/50 to-black/80'}`} />
        
        {isBigCard ? (
          <BigCardContent event={event} />
        ) : (
          <SmallCardContent event={event} />
        )}
      </div>
    );
  };
  
  const userRole = userData?.user?.role;
  const roleId = userRole?.id;
  const isPlus = userData?.isPlus || false;
  let planUntil = userData?.planValidUntil;
  const planEnded = planUntil ? (new Date(planUntil) < new Date()) : true;
  let duration = 'none';
  let include = '0';

  if (planUntil && roleId > 1 && roleId < 5) {
    duration = isPlus ? '3 months' : '1 month';
    if (planEnded) {
      duration = 'ended';
    } else if (isPlus) {
      include = '3';
    } else {
      include = '0';
    }
  } else {
    planUntil = null;
  }

  return (
    <EventWrapper
      title="Professional Networking"
      description="Connect with peers and industry experts through exclusive networking events."
      planDuration={duration}
      planIncludes={`${include}x events`}
      loading={loading}
      error={error}
      eventsCount={events.length}
      seeMoreLink="/professional-networking"
      seeMoreText="Find out more"
      loadingSkeletonCount={4} // Different count for ProfessionalNetworking
    >
      {/* Events Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start mb-8">
        {events.map((event, index) => (
          <EventCard key={event.id} event={event} index={index} />
        ))}
      </div>
    </EventWrapper>
  );
};

export default ProfessionalNetworking;